import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { settingsAPI, uploadAPI } from '../../services/apiService';
import { Settings, User, Lock, Globe, Facebook, Instagram, Twitter, Mail, Eye, EyeOff, CheckCircle, Image, BarChart3, Upload, X, Phone, MapPin, Clock, Briefcase, CreditCard, Edit3, FileText } from 'lucide-react';
import SiteImagesTab from '../../components/admin/SiteImagesTab';
import ContactInfoTab from '../../components/admin/ContactInfoTab';
import ServicesTab from '../../components/admin/ServicesTab';
import DocumentsTab from '../../components/admin/DocumentsTab';

const AdminSettings = () => {
  const { user } = useAuth();
  
  // Profile Settings
  const [profileData, setProfileData] = useState({
    username: user?.username || 'admin',
    email: 'info@fotografx.de',
    fullName: 'FotografX',
    bio: 'Professional photographer in Berlin specializing in weddings, portraits, and events.'
  });

  // Password Settings
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Social Media Settings
  const [socialMedia, setSocialMedia] = useState({
    instagram: 'https://www.instagram.com/fotografdex',
    facebook: 'https://www.facebook.com/fotografdex',
    twitter: 'https://www.twitter.com/fotografdex',
    email: 'info@fotografx.de'
  });

  // Site Settings
  const [siteSettings, setSiteSettings] = useState({
    siteName: 'FotografX',
    contactEmail: 'info@fotografx.de',
    contactPhone: '+49 (0) 123 456 789',
    contactAddress: 'Berlin, Deutschland',
    metaDescription: 'Professional photography services in Berlin'
  });

  // Impressum data
  const [impressumData, setImpressumData] = useState({
    name: 'Abdulla Abdullaev',
    company: 'DElightX – Fotografie & Design',
    street: 'Musterstraße 12',
    city: '07545 Gera',
    country: 'Deutschland',
    phone: '+49 123 456789',
    email: 'info@delightx.de',
    vatId: 'DE123456789',
    responsiblePerson: 'Abdulla Abdullaev'
  });

  // Site Images Settings
  const [siteImages, setSiteImages] = useState({
    hero_background_image: '',
    about_image: '',
    portfolio_category_image: '',
    services_category_image: '',
    reviews_category_image: '',
    blog_category_image: '',
    contacts_category_image: '',
    booking_category_image: ''
  });

  // Analytics Settings
  const [analytics, setAnalytics] = useState({
    google_analytics_id: ''
  });

  // Email Notifications
  const [emailSettings, setEmailSettings] = useState({
    sender_email: '',
    sender_password: '',
    admin_email: '',
    smtp_server: 'smtp.gmail.com',
    smtp_port: 587,
    configured: false
  });

  // Contact Settings
  const [contactSettings, setContactSettings] = useState({
    contact_email: '',
    contact_phone: '',
    contact_address: '',
    contact_hours: ''
  });

  // Services Settings
  const [services, setServices] = useState([]);



  // Site Branding Settings
  const [siteBranding, setSiteBranding] = useState({
    site_name: '',
    site_description_de: '',  
    site_description_ru: '',
    hero_title_de: '',
    hero_title_ru: '',
    real_estate_expertise_de: '',
    real_estate_expertise_ru: '',
    wedding_expertise_de: '',
    wedding_expertise_ru: ''
  });

  const [activeTab, setActiveTab] = useState('profile');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState('');

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      // Load profile settings
      try {
        const profileResponse = await settingsAPI.getProfile();
        if (profileResponse.data) {
          setProfileData(prev => ({
            ...prev,
            email: profileResponse.data.email || prev.email,
            fullName: profileResponse.data.fullName || prev.fullName,
            bio: profileResponse.data.bio || prev.bio
          }));
          // Load social links from profile
          if (profileResponse.data.social_links) {
            setSocialMedia(prev => ({
              instagram: profileResponse.data.social_links.instagram || prev.instagram,
              facebook: profileResponse.data.social_links.facebook || prev.facebook,
              twitter: profileResponse.data.social_links.linkedin || prev.twitter,
              email: profileResponse.data.social_links.whatsapp || prev.email
            }));
          }
        }
      } catch (err) {
        console.error('Error loading profile:', err);
      }

      // Load impressum
      try {
        const impressumResponse = await settingsAPI.getImpressum();
        if (impressumResponse.data) {
          setImpressumData(impressumResponse.data);
        }
      } catch (err) {
        console.error('Error loading impressum:', err);
      }

      // Load site images
      const imagesResponse = await settingsAPI.getSiteImages();
      setSiteImages(imagesResponse.data);

      // Load analytics
      const analyticsResponse = await settingsAPI.getAnalytics();
      setAnalytics(analyticsResponse.data);

      // Load contact settings
      const contactResponse = await settingsAPI.getContactSettings();
      setContactSettings(contactResponse.data);

      // Load services
      const servicesResponse = await settingsAPI.getServices();
      setServices(servicesResponse.data.services || []);

      // Load email notifications
      const emailResponse = await settingsAPI.getEmailNotifications();
      setEmailSettings(emailResponse.data);

      // Load site branding
      const brandingResponse = await settingsAPI.getSiteBranding();
      setSiteBranding(brandingResponse.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await settingsAPI.updateProfile(profileData);
      setMessage('Профиль обновлен успешно!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage('Ошибка обновления профиля');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    // Validate password requirements
    if (passwordData.newPassword.length < 8) {
      setMessage('Пароль должен содержать минимум 8 символов');
      return;
    }
    
    if (!/(?=.*\d)/.test(passwordData.newPassword)) {
      setMessage('Пароль должен содержать минимум одну цифру');
      return;
    }
    
    if (!/(?=.*[!@#$%^&*])/.test(passwordData.newPassword)) {
      setMessage('Пароль должен содержать минимум один специальный символ');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage('Новые пароли не совпадают');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await settingsAPI.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      setMessage('Пароль успешно изменен!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.response?.status === 400) {
        setMessage('Неверный текущий пароль');
      } else {
        setMessage('Ошибка при изменении пароля');
      }
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSocialMediaSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Use settingsAPI to update social links
      await settingsAPI.updateSocialLinks({
        instagram: socialMedia.instagram,
        facebook: socialMedia.facebook,
        twitter: socialMedia.twitter, // This maps to LinkedIn in backend
        email: socialMedia.email // This maps to WhatsApp in backend
      });
      setMessage('Ссылки социальных сетей обновлены успешно!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating social media:', error);
      setMessage('Ошибка обновления ссылок социальных сетей');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSiteSettingsSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // TODO: API call to update site settings
      console.log('Site settings updated:', siteSettings);
      setMessage('Настройки сайта обновлены успешно!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Ошибка обновления настроек сайта');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImpressumSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Use settingsAPI to update impressum
      await settingsAPI.updateImpressum(impressumData);
      setMessage('Impressum обновлен успешно!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating impressum:', error);
      setMessage('Ошибка обновления impressum');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageUpload = async (field, file) => {
    if (!file) return;
    
    setUploading(field);
    try {
      const response = await uploadAPI.uploadImage(file);
      const imageUrl = response.data.url;
      
      // Handle site images
      if (field.includes('category_image') || field.includes('hero') || field.includes('about')) {
        setSiteImages(prev => ({
          ...prev,
          [field]: imageUrl
        }));
      } 
      // Handle service images
      else if (field.startsWith('service_')) {
        const serviceIndex = parseInt(field.split('_')[1]);
        const updatedServices = [...services];
        updatedServices[serviceIndex].image = imageUrl;
        setServices(updatedServices);
      }
      
      setMessage('Изображение загружено успешно! Не забудьте сохранить настройки.');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error uploading image:', error);
      setMessage('Ошибка загрузки изображения');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setUploading('');
    }
  };

  const handleSiteImagesSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await settingsAPI.updateSiteImages(siteImages);
      setMessage('Изображения сайта обновлены успешно!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating site images:', error);
      setMessage('Ошибка обновления изображений сайта');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAnalyticsSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await settingsAPI.updateAnalytics(analytics);
      setMessage('Настройки аналитики обновлены успешно!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating analytics:', error);
      setMessage('Ошибка обновления аналитики');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await settingsAPI.updateContactSettings(contactSettings);
      setMessage('Контактные настройки обновлены успешно!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating contact settings:', error);
      setMessage('Ошибка обновления контактных настроек');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleServicesSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await settingsAPI.updateServices(services);
      setMessage('Услуги обновлены успешно!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating services:', error);
      setMessage('Ошибка обновления услуг');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };



  const handleSiteBrandingSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await settingsAPI.updateSiteBranding(siteBranding);
      setMessage('Брендинг сайта обновлен успешно!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating site branding:', error);
      setMessage('Ошибка обновления брендинга сайта');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Email Settings handlers
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await settingsAPI.updateEmailNotifications(emailSettings);
      setMessage('Настройки email уведомлений обновлены успешно!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating email settings:', error);
      setMessage('Ошибка обновления настроек email');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTestEmail = async () => {
    setIsSubmitting(true);
    try {
      const response = await settingsAPI.testEmail();
      if (response.success) {
        setMessage('Тестовое письмо отправлено успешно! Проверьте ваш email.');
      } else {
        setMessage(`Ошибка тестирования email: ${response.message}`);
      }
      setTimeout(() => setMessage(''), 5000);
    } catch (error) {
      console.error('Error testing email:', error);
      setMessage('Ошибка при тестировании email конфигурации');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabs = [
    { id: 'profile', name: 'Профиль', icon: User },
    { id: 'social', name: 'Социальные сети', icon: Globe },
    { id: 'images', name: 'Изображения сайта', icon: Image },
    { id: 'contact', name: 'Контактная информация', icon: Phone },
    { id: 'services', name: 'Услуги', icon: Briefcase },
    { id: 'documents', name: 'Документы', icon: FileText },
    { id: 'email', name: 'Email уведомления', icon: Mail },
    { id: 'branding', name: 'Брендинг сайта', icon: Edit3 },
    { id: 'analytics', name: 'Аналитика', icon: BarChart3 },
    { id: 'impressum', name: 'Impressum', icon: Settings }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Настройки</h1>
        <p className="text-gray-300">Управление настройками аккаунта и сайта</p>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-md border ${
          message.includes('Error') || message.includes('not match') || message.includes('must')
            ? 'bg-red-900 bg-opacity-50 text-red-200 border-red-700'
            : 'bg-green-900 bg-opacity-50 text-green-200 border-green-700'
        }`}>
          <div className="flex items-center">
            {!message.includes('Error') && !message.includes('not match') && !message.includes('must') && (
              <CheckCircle className="h-5 w-5 mr-3" />
            )}
            {message}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-400'
                    : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-gray-800 shadow-lg rounded-lg border border-gray-700">
        <div className="p-6">
          {/* Profile Tab (Combined with Password) */}
          {activeTab === 'profile' && (
            <div className="space-y-8">
              {/* Profile Information Section */}
              <form onSubmit={handleProfileSubmit} className="space-y-6">
                <h3 className="text-lg font-medium text-white">Информация профиля</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Имя пользователя
                    </label>
                    <input
                      type="text"
                      value={profileData.username}
                      onChange={(e) => setProfileData({...profileData, username: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      disabled
                    />
                    <p className="text-xs text-gray-400 mt-1">Имя пользователя нельзя изменить</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors duration-200"
                  >
                    {isSubmitting ? 'Сохранение...' : 'Сохранить профиль'}
                  </button>
                </div>
              </form>

              {/* Divider */}
              <div className="border-t border-gray-700"></div>

              {/* Password Change Section */}
              <form onSubmit={handlePasswordSubmit} className="space-y-6">
                <h3 className="text-lg font-medium text-white">Изменить пароль</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Текущий пароль
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                      className="w-full px-3 py-2 pr-10 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                    >
                      {showPasswords.current ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Новый пароль
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.new ? 'text' : 'password'}
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                        className="w-full px-3 py-2 pr-10 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        required
                        minLength={8}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                      >
                        {showPasswords.new ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Минимум 8 символов, минимум одна цифра и один специальный символ
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Подтвердить новый пароль
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                        className="w-full px-3 py-2 pr-10 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        required
                        minLength={8}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                      >
                        {showPasswords.confirm ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors duration-200"
                  >
                    {isSubmitting ? 'Изменение...' : 'Изменить пароль'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Social Media Tab */}
          {activeTab === 'social' && (
            <form onSubmit={handleSocialMediaSubmit} className="space-y-6">
              <h3 className="text-lg font-medium text-white">Социальные сети</h3>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Instagram className="h-5 w-5 text-pink-500" />
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Instagram
                    </label>
                    <input
                      type="url"
                      value={socialMedia.instagram}
                      onChange={(e) => setSocialMedia({...socialMedia, instagram: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="https://www.instagram.com/вашеимя"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Facebook className="h-5 w-5 text-blue-500" />
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Facebook
                    </label>
                    <input
                      type="url"
                      value={socialMedia.facebook}
                      onChange={(e) => setSocialMedia({...socialMedia, facebook: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="https://www.facebook.com/вашастраница"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Twitter className="h-5 w-5 text-blue-400" />
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      LinkedIn
                    </label>
                    <input
                      type="url"
                      value={socialMedia.twitter}
                      onChange={(e) => setSocialMedia({...socialMedia, twitter: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="https://www.linkedin.com/in/вашеимя"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-green-500" />
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Номер WhatsApp
                    </label>
                    <input
                      type="tel"
                      value={socialMedia.email}
                      onChange={(e) => setSocialMedia({...socialMedia, email: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="+1234567890"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Включите код страны (например, +1234567890)
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors duration-200"
                >
                  {isSubmitting ? 'Сохранение...' : 'Сохранить социальные сети'}
                </button>
              </div>
            </form>
          )}

          {/* Impressum Tab */}
          {activeTab === 'impressum' && (
            <form onSubmit={handleImpressumSubmit} className="space-y-6">
              <h3 className="text-lg font-medium text-white">Настройки Impressum</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Имя *
                  </label>
                  <input
                    type="text"
                    value={impressumData.name}
                    onChange={(e) => setImpressumData({...impressumData, name: e.target.value})}
                    required
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Компания *
                  </label>
                  <input
                    type="text"
                    value={impressumData.company}
                    onChange={(e) => setImpressumData({...impressumData, company: e.target.value})}
                    required
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Адрес улицы *
                  </label>
                  <input
                    type="text"
                    value={impressumData.street}
                    onChange={(e) => setImpressumData({...impressumData, street: e.target.value})}
                    required
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Город и почтовый индекс *
                  </label>
                  <input
                    type="text"
                    value={impressumData.city}
                    onChange={(e) => setImpressumData({...impressumData, city: e.target.value})}
                    required
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Страна *
                  </label>
                  <input
                    type="text"
                    value={impressumData.country}
                    onChange={(e) => setImpressumData({...impressumData, country: e.target.value})}
                    required
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Телефон *
                  </label>
                  <input
                    type="tel"
                    value={impressumData.phone}
                    onChange={(e) => setImpressumData({...impressumData, phone: e.target.value})}
                    required
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={impressumData.email}
                    onChange={(e) => setImpressumData({...impressumData, email: e.target.value})}
                    required
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    НДС ID *
                  </label>
                  <input
                    type="text"
                    value={impressumData.vatId}
                    onChange={(e) => setImpressumData({...impressumData, vatId: e.target.value})}
                    required
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Ответственное лицо *
                </label>
                <input
                  type="text"
                  value={impressumData.responsiblePerson}
                  onChange={(e) => setImpressumData({...impressumData, responsiblePerson: e.target.value})}
                  required
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors duration-200"
                >
                  {isSubmitting ? 'Сохранение...' : 'Сохранить Impressum'}
                </button>
              </div>
            </form>
          )}

          {/* Site Images Tab */}
          {activeTab === 'images' && (
            <SiteImagesTab 
              siteImages={siteImages}
              setSiteImages={setSiteImages}
              handleImageUpload={handleImageUpload}
              uploading={uploading}
              handleSiteImagesSubmit={handleSiteImagesSubmit}
              isSubmitting={isSubmitting}
            />
          )}

          {/* Contact Info Tab */}
          {activeTab === 'contact' && (
            <ContactInfoTab 
              contactSettings={contactSettings}
              setContactSettings={setContactSettings}
              handleContactSubmit={handleContactSubmit}
              isSubmitting={isSubmitting}
            />
          )}

          {/* Services Tab */}
          {activeTab === 'services' && (
            <ServicesTab 
              services={services}
              setServices={setServices}
              handleServicesSubmit={handleServicesSubmit}
              isSubmitting={isSubmitting}
              handleImageUpload={handleImageUpload}
              uploading={uploading}
            />
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <DocumentsTab />
          )}

          {/* Email Notifications Tab */}
          {activeTab === 'email' && (
            <form onSubmit={handleEmailSubmit} className="space-y-6">
              <h3 className="text-lg font-medium text-white">Настройки Email уведомлений</h3>
              <p className="text-gray-400 text-sm">Настройте отправку email уведомлений при получении новых заявок и контактных форм.</p>
              
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Email отправителя
                    </label>
                    <input
                      type="email"
                      value={emailSettings.sender_email}
                      onChange={(e) => setEmailSettings({...emailSettings, sender_email: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="noreply@yourdomain.com"
                      required
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Email от имени которого будут отправляться уведомления
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Пароль приложения
                    </label>
                    <input
                      type="password"
                      value={emailSettings.sender_password}
                      onChange={(e) => setEmailSettings({...emailSettings, sender_password: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="••••••••••••••••"
                      required
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Для Gmail используйте пароль приложения, не основной пароль
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Email администратора
                  </label>
                  <input
                    type="email"
                    value={emailSettings.admin_email}
                    onChange={(e) => setEmailSettings({...emailSettings, admin_email: e.target.value})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="admin@yourdomain.com"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    На этот email будут приходить уведомления о новых заявках
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      SMTP Сервер
                    </label>
                    <input
                      type="text"
                      value={emailSettings.smtp_server}
                      onChange={(e) => setEmailSettings({...emailSettings, smtp_server: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="smtp.gmail.com"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      SMTP Порт
                    </label>
                    <input
                      type="number"
                      value={emailSettings.smtp_port}
                      onChange={(e) => setEmailSettings({...emailSettings, smtp_port: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="587"
                      required
                    />
                  </div>
                </div>

                <div className="bg-gray-900 p-4 border border-gray-700 rounded-md">
                  <h4 className="text-sm font-medium text-white mb-2">Настройка Gmail:</h4>
                  <ol className="text-xs text-gray-400 space-y-1 list-decimal list-inside">
                    <li>Включите двухэтапную аутентификацию в вашем аккаунте Google</li>
                    <li>Перейдите в <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300">Пароли приложений</a></li>
                    <li>Создайте новый пароль приложения для "Почта"</li>
                    <li>Используйте сгенерированный пароль в поле "Пароль приложения" выше</li>
                    <li>SMTP сервер: smtp.gmail.com, порт: 587</li>
                  </ol>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleTestEmail}
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors duration-200"
                >
                  {isSubmitting ? 'Тестирование...' : 'Тест Email'}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors duration-200"
                >
                  {isSubmitting ? 'Сохранение...' : 'Сохранить настройки'}
                </button>
              </div>
            </form>
          )}

          {/* Site Branding Tab */}
          {activeTab === 'branding' && (
            <form onSubmit={handleSiteBrandingSubmit} className="space-y-6">
              <h3 className="text-lg font-medium text-white">Брендинг сайта</h3>
              <p className="text-gray-400 text-sm">Настройте название вашего веб-сайта и описания, которые появляются на всем сайте.</p>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Название сайта
                  </label>
                  <input
                    type="text"
                    value={siteBranding.site_name}
                    onChange={(e) => setSiteBranding({...siteBranding, site_name: e.target.value})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="FotografX"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Это будет отображаться в шапке, подвале и названиях страниц на всем веб-сайте.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Описание сайта (немецкий)
                  </label>
                  <textarea
                    value={siteBranding.site_description_de}
                    onChange={(e) => setSiteBranding({...siteBranding, site_description_de: e.target.value})}
                    rows={2}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Professionelle Fotografie für Ihre besonderen Momente"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Описание сайта (русский)
                  </label>
                  <textarea
                    value={siteBranding.site_description_ru}
                    onChange={(e) => setSiteBranding({...siteBranding, site_description_ru: e.target.value})}
                    rows={2}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Профессиональная фотография для ваших особенных моментов"
                    required
                  />
                </div>

                {/* Hero Title Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      🎯 Заголовок Hero (немецкий)
                    </label>
                    <textarea
                      value={siteBranding.hero_title_de || ''}
                      onChange={(e) => setSiteBranding({...siteBranding, hero_title_de: e.target.value})}
                      rows={2}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Hochzeitsfotograf in Gera und Umgebung"
                      required
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Основной заголовок H1 на главной странице (немецкий)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      🎯 Заголовок Hero (русский)
                    </label>
                    <textarea
                      value={siteBranding.hero_title_ru || ''}
                      onChange={(e) => setSiteBranding({...siteBranding, hero_title_ru: e.target.value})}
                      rows={2}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Свадебный фотограф в Гере и окрестностях"
                      required
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Основной заголовок H1 на главной странице (русский)
                    </p>
                  </div>
                </div>

                {/* Real Estate Expertise Fields (Primary) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      🏠 Экспертиза по недвижимости (немецкий) - ОСНОВНАЯ УСЛУГА
                    </label>
                    <textarea
                      value={siteBranding.real_estate_expertise_de || ''}
                      onChange={(e) => setSiteBranding({...siteBranding, real_estate_expertise_de: e.target.value})}
                      rows={4}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Spezialisiert auf hochwertige Immobilienfotografie..."
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Основное описание услуг по фотографии недвижимости (немецкий)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      🏠 Экспертиза по недвижимости (русский) - ОСНОВНАЯ УСЛУГА
                    </label>
                    <textarea
                      value={siteBranding.real_estate_expertise_ru || ''}
                      onChange={(e) => setSiteBranding({...siteBranding, real_estate_expertise_ru: e.target.value})}
                      rows={4}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Специализируется на качественной фотографии недвижимости..."
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Основное описание услуг по фотографии недвижимости (русский)
                    </p>
                  </div>
                </div>

                {/* Wedding Expertise Fields (Secondary Service) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      🎉 Свадебная экспертиза (немецкий) - ДОПОЛНИТЕЛЬНАЯ УСЛУГА
                    </label>
                    <textarea
                      value={siteBranding.wedding_expertise_de || ''}
                      onChange={(e) => setSiteBranding({...siteBranding, wedding_expertise_de: e.target.value})}
                      rows={4}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Текст о вашем опыте в свадебной фотографии на немецком языке..."
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Отображается в блоке "Ihre Hochzeit in perfekten Bildern" на главной странице
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      🎉 Свадебная экспертиза (русский) - ДОПОЛНИТЕЛЬНАЯ УСЛУГА
                    </label>
                    <textarea
                      value={siteBranding.wedding_expertise_ru || ''}
                      onChange={(e) => setSiteBranding({...siteBranding, wedding_expertise_ru: e.target.value})}
                      rows={4}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Текст о вашем опыте в свадебной фотографии на русском языке..."
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Отображается в блоке "Ваша свадьба в совершенных снимках" на главной странице
                    </p>
                  </div>
                </div>

                <div className="bg-gray-900 p-4 border border-gray-700 rounded-md">
                  <h4 className="text-sm font-medium text-white mb-2">Примечание:</h4>
                  <p className="text-xs text-gray-400">
                    Эти изменения обновят название и описания вашего сайта в навигации заголовка, подвале, названиях страниц и других местах на вашем веб-сайте.
                  </p>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors duration-200"
                >
                  {isSubmitting ? 'Сохранение...' : 'Сохранить брендинг сайта'}
                </button>
              </div>
            </form>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <form onSubmit={handleAnalyticsSubmit} className="space-y-6">
              <h3 className="text-lg font-medium text-white">Настройки аналитики</h3>
              <p className="text-gray-400 text-sm">Настройте Google Analytics для отслеживания посетителей и производительности вашего веб-сайта.</p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Measurement ID Google Analytics
                  </label>
                  <input
                    type="text"
                    value={analytics.google_analytics_id}
                    onChange={(e) => setAnalytics({...analytics, google_analytics_id: e.target.value})}
                    placeholder="G-XXXXXXXXXX"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Найдите ваш Measurement ID в Google Analytics → Админ → Настройки недвижимости → Детали недвижимости
                  </p>
                </div>

                <div className="bg-gray-900 p-4 border border-gray-700 rounded-md">
                  <h4 className="text-sm font-medium text-white mb-2">Как получить ваш Google Analytics ID:</h4>
                  <ol className="text-xs text-gray-400 space-y-1">
                    <li>1. Перейдите в <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300">Google Analytics</a></li>
                    <li>2. Создайте новую недвижимость для вашего веб-сайта</li>
                    <li>3. Скопируйте Measurement ID (начинается с "G-")</li>
                    <li>4. Вставьте его в поле выше и сохраните</li>
                  </ol>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors duration-200"
                >
                  {isSubmitting ? 'Сохранение...' : 'Сохранить настройки аналитики'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;